import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { reaction, observable } from 'mobx'
import get from 'lodash/get'
import { NULL as BlockModelNULL } from '@client/components/Block/model'

import VideoModel, { NULL as VideoModelNULL } from './model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { SECTION_TYPES } from '../../Section/constants'
import { getPaddingTotal } from '../../Block/component'
import reservedImageHeight from '@client/utils/reservedImageHeight'

const VIDEO_PLACEHOLDER_PATH = 'https://cdn.getanewsletter.com/blockeditor/placeholder-video.png'

const getPaddingReduction = ({ kind, section, columnIndex, fullWidth }) => {
  if (fullWidth) {
    return 0
  }
  const sectionType = get(section, 'sectionType')
  if (
    sectionType === SECTION_TYPES.LANDINGONECOL ||
    sectionType === SECTION_TYPES.LANDINGTWOCOL
  ) {
    return 0
  }
  return getPaddingTotal({ kind, section, columnIndex, fullWidth })
}

export class Video extends Component {
  @observable editingSettings = BlockModelNULL

  static propTypes = {
    model: PropTypes.instanceOf(VideoModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: VideoModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  disposeReaction = null

  constructor(props) {
    super(props)

    this.el = React.createRef()

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL

    this.disposeReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )

    // Toggling fullWidth changes the padding reduction in setContainerDimentions
    // but does NOT physically resize the observed tableCell, so ResizeObserver
    // won't fire. Use a mobx reaction (not componentDidUpdate prevProps) — under
    // @observer, prevProps.model and this.props.model reference the same
    // instance, so prevProps.model.settings.values.fullWidth resolves to the
    // new value too and the comparison would always be equal.
    const model = props.model
    this.disposeFullWidthReaction = reaction(
      () => model.settings.values.fullWidth,
      () => {
        this.setContainerDimentions()
      }
    )
  }

  componentDidMount() {
    this.tableCell = this.getTableCell()
    this.setContainerDimentions()
    if (this.tableCell) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.tableCell)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeReaction) {
      this.disposeReaction()
    }
    if (this.disposeFullWidthReaction) {
      this.disposeFullWidthReaction()
    }
  }

  getTableCell = () => {
    return get(this.el, 'current.parentNode')
  }

  setContainerDimentions() {
    const section = get(this.props, 'model.section')
    const columnIndex = this.props.model.columnIndex
    const kind = this.props.model.kind
    const fullWidth = this.props.model.settings.values.fullWidth

    const padding = getPaddingReduction({ kind, section, columnIndex, fullWidth })

    const tableCell = this.tableCell
    const clientWidth = get(tableCell, 'clientWidth')

    this.props.model.setContainerDimensions({
      width: clientWidth - padding,
    })
  }

  render() {
    const { model, serverRender } = this.props
    const globalSettings = this.props.editor.settings.values
    const settings = model.settings.values
    const width = parseFloat(settings.width) || undefined
    const videoUrl = settings.videoUrl ? settings.videoUrl.trim() : ''
    const useUserUrl = !!(settings.imageUrl && settings.imageUrl.trim())
    const imageUrl = useUserUrl ? settings.imageUrl.trim() : VIDEO_PLACEHOLDER_PATH
    // Reserve the rendered box from the intrinsic dimensions captured at
    // thumbnail-set time so a blocked/images-off thumbnail doesn't collapse to
    // 0px (FE-1128). Skipped for the placeholder and when dimensions aren't
    // known yet.
    const reservedHeight = useUserUrl
      ? reservedImageHeight(width, settings.naturalWidth, settings.naturalHeight)
      : undefined
    const altText = settings.altText ? settings.altText.trim() : ''

    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const fullWidthClass = this.props.model.settings.values.fullWidth
      ? 'full-width'
      : ''
    const className = `${serverRender ? '' : 'video'} ${fullWidthClass}`

    const imgStyle = {
      minHeight: serverRender ? undefined : '20px',
      display: 'block',
      border: 0,
      height: 'auto',
      margin: 0,
      width: width ? `${width}px` : undefined,
      fontSize: fontSize + 'px',
    }

    return (
      <Choose>
        <When condition={videoUrl}>
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block' }}
            ref={this.el}
          >
            <img
              className={className}
              src={imageUrl}
              alt={altText}
              width={width}
              height={reservedHeight}
              style={imgStyle}
            />
          </a>
        </When>
        <When condition={!videoUrl}>
          <img
            className={className}
            src={imageUrl}
            alt={altText}
            width={width}
            height={reservedHeight}
            style={imgStyle}
            ref={this.el}
          />
        </When>
      </Choose>
    )
  }
}

export default inject('app', 'editor', 'serverRender')(observer(Video))
